import { ArrowRight, CheckCircle2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/cn';

/**
 * O que precisa de ação no período — uma lista, não quatro cartões.
 *
 * Só entra na lista o que tem contagem: alerta zerado não é alerta, é silêncio, e ganha
 * uma linha só, no fim, dizendo que está em dia. Quatro cartões iguais com "0" em verde
 * em três deles faziam o olho procurar o problema entre os não-problemas.
 *
 * Cada linha diz o número, o que ele significa e para onde ir para agir — e o link diz o
 * nome da tela, não "abrir".
 */
export interface AttentionItem {
  readonly key: string;
  readonly count: number;
  /** A frase já concordada com o número: "recursos acima da jornada". */
  readonly title: string;
  /** O complemento: quanto, desde quando, o que fazer. */
  readonly detail: string;
  /** Como esta linha aparece quando está em dia: "ninguém ocioso". */
  readonly quietLabel: string;
  readonly tone: 'danger' | 'warn';
  readonly to: string;
  readonly linkLabel: string;
  readonly icon: ReactNode;
}

const TONE = {
  danger: { rail: 'bg-(--color-state-danger)', text: 'text-(--color-state-danger)' },
  warn: { rail: 'bg-(--color-state-warn)', text: 'text-(--color-state-warn)' },
} as const;

export function AttentionList({ items }: { items: readonly AttentionItem[] }) {
  const active = items.filter((item) => item.count > 0);
  const quiet = items.filter((item) => item.count === 0);

  return (
    <Card>
      {active.length === 0 ? (
        <p className="flex items-center gap-2 p-4 text-sm text-(--color-content-muted)">
          <CheckCircle2 className="size-4 shrink-0 text-(--color-state-ok)" aria-hidden="true" />
          Nada exige ação neste período.
        </p>
      ) : (
        <ul className="divide-y divide-(--color-border-subtle)">
          {active.map((item) => {
            const tone = TONE[item.tone];
            return (
              <li
                key={item.key}
                className="relative flex flex-wrap items-center gap-x-4 gap-y-1 py-3 pl-5 pr-4"
              >
                <span
                  className={cn('absolute inset-y-3 left-0 w-1 rounded-r', tone.rail)}
                  aria-hidden="true"
                />
                {/* Texto e link dividem a linha no monitor; no celular o link desce, em vez
                    de espremer a frase numa coluna de três palavras. */}
                <div className="flex min-w-0 flex-1 basis-64 items-center gap-3">
                  <span
                    className={cn('flex size-8 shrink-0 items-center justify-center', tone.text)}
                  >
                    {item.icon}
                  </span>
                  <p className="flex min-w-0 flex-wrap items-baseline gap-x-2">
                    <span className={cn('tabular text-xl font-semibold leading-none', tone.text)}>
                      {item.count}
                    </span>
                    <span className="text-sm font-medium">{item.title}</span>
                    <span className="text-sm text-(--color-content-muted)">{item.detail}</span>
                  </p>
                </div>
                <Link
                  to={item.to}
                  className="inline-flex min-h-11 shrink-0 items-center gap-1 pl-11 text-sm text-(--color-brand) underline-offset-4 hover:underline print:hidden md:min-h-0 md:pl-0"
                >
                  {item.linkLabel}
                  <ArrowRight className="size-3.5" aria-hidden="true" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {quiet.length > 0 && active.length > 0 && (
        <p className="flex items-center gap-2 border-t border-(--color-border-subtle) px-4 py-2.5 text-xs text-(--color-content-muted)">
          <CheckCircle2 className="size-3.5 shrink-0 text-(--color-state-ok)" aria-hidden="true" />
          Em dia: {quiet.map((item) => item.quietLabel).join(' · ')}
        </p>
      )}
    </Card>
  );
}
